import PublicLayout from "@/components/layout/PublicLayout";
import { useState } from "react";
import { Link } from "react-router-dom";
import { requestPasswordReset } from "@/lib/api";
import { track } from "@/lib/analytics";
import { Mail, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setLoading(true);
        try {
            await requestPasswordReset(email.trim().toLowerCase());
            track("password_reset_requested");
        } catch {
            // Non-disclosing: even on error, show the success copy so we never
            // leak whether an account exists.
        } finally {
            setSubmitted(true);
            setLoading(false);
        }
    };

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div
                    className="bl-container py-16 max-w-md"
                    data-testid="forgot-password-page"
                >
                    <Link
                        to="/auth/login"
                        className="inline-flex items-center gap-2 text-sm text-bl-muted hover:text-bl-primary mb-6"
                        data-testid="forgot-back-to-signin"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to sign in
                    </Link>
                    <div className="bl-eyebrow">Account recovery</div>
                    <h1 className="mt-4 font-display font-semibold text-3xl sm:text-4xl tracking-tight text-bl leading-tight">
                        Reset your{" "}
                        <span className="font-italic-accent text-bl-primary">
                            password
                        </span>
                        .
                    </h1>
                    <p className="mt-4 text-bl-muted leading-relaxed">
                        Enter the email associated with your Buyer's Leverage
                        account and we'll send a secure reset link.
                    </p>

                    {submitted ? (
                        <div
                            className="mt-8 bl-card p-7 text-center"
                            data-testid="forgot-success-card"
                        >
                            <div className="mx-auto w-12 h-12 rounded-full bg-bl-primary/10 flex items-center justify-center">
                                <Mail className="w-6 h-6 text-bl-primary" />
                            </div>
                            <h2 className="mt-4 text-xl font-display font-semibold text-bl">
                                Check your email
                            </h2>
                            <p className="mt-3 text-sm text-bl-muted leading-relaxed">
                                If an account exists for{" "}
                                <span className="text-bl">{email}</span>, we'll
                                send a password reset link shortly. The link
                                expires in 1 hour.
                            </p>
                            <p className="mt-4 text-xs text-bl-muted">
                                Didn't see it? Check your spam folder or{" "}
                                <button
                                    type="button"
                                    onClick={() => setSubmitted(false)}
                                    className="text-bl-primary hover:underline"
                                    data-testid="forgot-try-different-email"
                                >
                                    try a different email
                                </button>
                                .
                            </p>
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit}
                            className="mt-8 bl-card p-6 md:p-7 space-y-4"
                        >
                            <div>
                                <label className="text-[11px] tracking-[0.18em] uppercase text-bl-muted">
                                    Email
                                </label>
                                <input
                                    required
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="mt-2 w-full px-4 py-3 bg-bl-bg rounded-lg border border-bl text-bl outline-none focus:border-bl-primary"
                                    data-testid="forgot-email-input"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="bl-btn-primary w-full"
                                data-testid="forgot-submit-btn"
                            >
                                {loading ? "Sending…" : "Send reset link"}
                            </button>
                            <p className="text-center text-xs text-bl-muted pt-2">
                                Not sure which email you used?{" "}
                                <Link
                                    to="/recover-account"
                                    className="text-bl-primary hover:underline"
                                    data-testid="forgot-recover-account-link"
                                >
                                    Recover your account
                                </Link>
                            </p>
                        </form>
                    )}
                </div>
            </section>
        </PublicLayout>
    );
}
